import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import MenuOpenRoundedIcon from "@mui/icons-material/MenuOpenRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import FlexBetween from "./FlexBetween";
import { useStateContext } from "../contexts/ContextProvider";

const ANIMATION_SPEED = 50;

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor:
      theme.palette.mode === "dark"
        ? theme.palette.background.alt
        : theme.palette.background.paper,
    color: theme.palette.primary.main,
    maxWidth: 340,
    boxShadow: theme.shadows[1],
    fontSize: theme.typography.pxToRem(14),
    padding: "8px 12px",
  },
}));

const MenuItem = ({
  icon,
  text,
  isActive,
  onClick,
  isCollapsed,
  textVisible,
  tourId,
}) => {
  const theme = useTheme();

  return (
    <Box
      data-tour-id={tourId}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        borderRadius: "12px",
        mx: isCollapsed ? 1.25 : 2,
        my: 0.25,
        py: 1,
        px: 1.25,
        backgroundColor: isActive ? theme.palette.primary.main : "transparent",
        color: isActive
          ? theme.palette.primary.main
          : theme.palette.primary.text2,
        transition: "background-color 0.3s ease",
        "&:hover": {
          backgroundColor: isActive
            ? theme.palette.primary.buttonHoverSelected
            : theme.palette.primary.buttonHover,
        },
      }}
    >
      <Box
        sx={{
          width: "40px",
          height: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          color: isActive
            ? theme.palette.background.default
            : theme.palette.primary.text2,
        }}
      >
        {icon}
      </Box>

      {!isCollapsed && (
        <Box
          sx={{
            ml: 1,
            flexGrow: 1,
            opacity: textVisible ? 1 : 0,
            transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isActive
                ? theme.palette.background.default
                : theme.palette.primary.text2,
            }}
          >
            {text}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const SectionHeader = ({ text, isCollapsed, textVisible }) => {
  const theme = useTheme();

  if (isCollapsed) return null;

  return (
    <Box
      sx={{
        mx: 2,
        mt: 0.5,
        mb: 0.125,
        opacity: textVisible ? 1 : 0,
        transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.primary.main,
          fontWeight: "bold",
          fontSize: 13,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const SidebarLinguist = ({ user, drawerWidth, isNonMobile }) => {
  const collapsedDrawerWidth = Math.floor(drawerWidth * 0.25);
  const {
    activeMenu,
    setActiveMenu,
    isCollapsed: contextIsCollapsed,
    setIsCollapsed: setContextIsCollapsed,
  } = useStateContext();

  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const navItems = [
    {
      text: "WORKSPACE",
      link: "",
      icon: null,
    },
    {
      text: "Dashboard",
      link: "linguist-portal",
      icon: <DashboardRoundedIcon />,
      tourId: "nav-dashboard",
    },
    {
      text: "Interpretations",
      link: "linguist-portal/interpretations",
      icon: <RecordVoiceOverRoundedIcon />,
      tourId: "nav-interpretations",
    },
    {
      text: "SETTINGS",
      link: "",
      icon: null,
    },
    {
      text: "Profile Settings",
      link: "linguist-portal/profile",
      icon: <PersonRoundedIcon />,
      tourId: "nav-profile",
    },
  ];

  const [isCollapsed, setIsCollapsed] = useState(contextIsCollapsed);
  const [textVisible, setTextVisible] = useState(!contextIsCollapsed);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activePath, setActivePath] = useState("");

  useEffect(() => {
    if (
      contextIsCollapsed === isCollapsed &&
      textVisible === !contextIsCollapsed
    ) {
      return;
    }

    setIsAnimating(true);

    if (contextIsCollapsed) {
      setTextVisible(false);

      const timer = setTimeout(() => {
        setIsCollapsed(true);
        setIsAnimating(false);
      }, ANIMATION_SPEED);

      return () => clearTimeout(timer);
    }

    setIsCollapsed(false);

    const timer = setTimeout(() => {
      setTextVisible(true);
      setIsAnimating(false);
    }, ANIMATION_SPEED);

    return () => clearTimeout(timer);
  }, [contextIsCollapsed, isCollapsed, textVisible]);

  useEffect(() => {
    setActivePath(String(pathname || "").replace(/^\//, ""));
  }, [pathname]);

  const currentDrawerWidth = isCollapsed ? collapsedDrawerWidth : drawerWidth;

  const isActiveLink = (link) => {
    if (link === "linguist-portal") {
      return activePath === "linguist-portal";
    }

    if (link === "linguist-portal/interpretations") {
      return (
        activePath.startsWith("linguist-portal/interpretations") ||
        activePath.startsWith("linguist-portal/assignments")
      );
    }

    return activePath.startsWith(link);
  };

  const handleToggleSidebar = () => {
    if (isAnimating) return;

    if (!isNonMobile) {
      setActiveMenu(false);
      return;
    }

    if (!activeMenu) {
      setActiveMenu(true);
      setContextIsCollapsed(false);
    } else if (!contextIsCollapsed) {
      setContextIsCollapsed(true);
    } else {
      setContextIsCollapsed(false);
    }
  };

  const handleNavigation = (link) => {
    if (!link) return;

    navigate(`/${link}`);

    if (!isNonMobile) {
      setActiveMenu(false);
    }
  };

  return (
    <>
      <Box
        component="nav"
        sx={{
          width: activeMenu ? currentDrawerWidth : 0,
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 1200,
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.easeInOut,
              duration: ANIMATION_SPEED,
            }),
        }}
      >
        {activeMenu && (
          <Drawer
            open={activeMenu}
            variant={!isNonMobile ? "temporary" : "permanent"}
            anchor="left"
            onClose={() => !isNonMobile && setActiveMenu(false)}
            PaperProps={{
              elevation: 10,
              sx: {
                width: currentDrawerWidth,
                border: "none",
                boxSizing: "border-box",
                transition: (theme) =>
                  theme.transitions.create("width", {
                    easing: theme.transitions.easing.easeInOut,
                    duration: ANIMATION_SPEED,
                  }),
              },
            }}
            sx={{
              width: currentDrawerWidth,
              "& .MuiDrawer-paper": {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.default,
                width: currentDrawerWidth,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
              }}
            >
              <Box p={isCollapsed ? 1 : 2} pb={isCollapsed ? 1 : 3}>
                <FlexBetween color={theme.palette.primary.main}>
                  {isCollapsed ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <img
                        src="/assets/ce-logo.svg"
                        alt="CE Portal Logo"
                        style={{
                          height: "32px",
                          width: "auto",
                          maxWidth: "150px",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        opacity: textVisible ? 1 : 0,
                        transition: `opacity ${ANIMATION_SPEED}ms ease-in-out`,
                        whiteSpace: "nowrap",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1.5,
                        }}
                      >
                        <img
                          src="/assets/ce-logo.svg"
                          alt="CE Portal Logo"
                          style={{
                            height: "32px",
                            width: "auto",
                            maxWidth: "150px",
                          }}
                        />
                        <Box>
                          <Typography
                            variant="h4"
                            fontWeight="bold"
                            sx={{ color: theme.palette.primary.main }}
                          >
                            CE Linguist Portal
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body"
                        sx={{ color: theme.palette.primary.text, ml: 0.25, mt: 1 }}
                      >
                        {`Welcome, ${user?.Name || user?.name || "Linguist User"}`}
                      </Typography>
                    </Box>
                  )}
                </FlexBetween>
              </Box>

              <Box sx={{ flexGrow: 1, overflow: "auto" }}>
                {navItems.map((item, index) => {
                  if (!item.icon) {
                    return (
                      <React.Fragment key={`section-${index}`}>
                        {isCollapsed &&
                          index > 0 &&
                          navItems[index - 1]?.icon && (
                            <Divider sx={{ my: 1, mx: 0.5 }} />
                          )}
                        <SectionHeader
                          text={item.text}
                          isCollapsed={isCollapsed}
                          textVisible={textVisible}
                        />
                      </React.Fragment>
                    );
                  }

                  const menuItem = (
                    <MenuItem
                      key={item.text}
                      icon={item.icon}
                      text={item.text}
                      isActive={isActiveLink(item.link)}
                      onClick={() => handleNavigation(item.link)}
                      isCollapsed={isCollapsed}
                      textVisible={textVisible}
                      tourId={item.tourId}
                    />
                  );

                  return isCollapsed ? (
                    <HtmlTooltip
                      key={`tooltip-${item.text}`}
                      title={item.text}
                      placement="right"
                    >
                      <Box>{menuItem}</Box>
                    </HtmlTooltip>
                  ) : (
                    menuItem
                  );
                })}
              </Box>

              <Box>
                <Divider sx={{ my: 1 }} />

                {isCollapsed ? (
                  <Box sx={{ mx: 0.5, my: 0.5 }}>
                    <HtmlTooltip title="Open Sidebar" placement="right">
                      <IconButton
                        onClick={handleToggleSidebar}
                        disabled={isAnimating}
                        sx={{
                          width: "100%",
                          borderRadius: "12px",
                          p: 1.5,
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.buttonHover,
                          },
                        }}
                      >
                        <MenuRoundedIcon />
                      </IconButton>
                    </HtmlTooltip>
                  </Box>
                ) : (
                  <Box sx={{ mx: 2, my: 0.5 }}>
                    <HtmlTooltip title="Close Sidebar" placement="right">
                      <IconButton
                        onClick={() =>
                          isNonMobile
                            ? handleToggleSidebar()
                            : setActiveMenu(false)
                        }
                        disabled={isAnimating}
                        sx={{
                          width: "100%",
                          borderRadius: "12px",
                          p: 1.5,
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.buttonHover,
                          },
                        }}
                      >
                        <MenuOpenRoundedIcon />
                      </IconButton>
                    </HtmlTooltip>
                  </Box>
                )}
              </Box>
            </Box>
          </Drawer>
        )}
      </Box>
    </>
  );
};

export default SidebarLinguist;
